import React from "react";
import { ArrowUpRight, Camera, Circle, Cloud, Hand, Highlighter, ListChecks, MapPin, Pen, Square, StickyNote, Stamp } from "lucide-react";

export const TOOLS = [
  { id: "pan", label: "Select / pan", Icon: Hand },
  { id: "pen", label: "Pen", Icon: Pen },
  { id: "highlighter", label: "Highlighter", Icon: Highlighter },
  { id: "shape", label: "Shape — square, cloud or circle", Icon: Cloud },
  { id: "arrow", label: "Arrow", Icon: ArrowUpRight },
  { id: "sticky", label: "Sticky note", Icon: StickyNote },
  { id: "photo_pin", label: "Photo pin", Icon: Camera },
  { id: "stamp", label: "Stamp / symbol", Icon: Stamp },
  { id: "punch_pin", label: "Punch pin", Icon: ListChecks },
];

const SHAPES = [
  { id: "square", label: "Square", Icon: Square },
  { id: "cloud", label: "Cloud", Icon: Cloud },
  { id: "circle", label: "Circle", Icon: Circle },
];

const COLORS = ["#b91c1c", "#1d4ed8", "#15803d", "#f59e0b", "#7c3aed", "#111827"];

/** One-handed toolbar: big targets, bottom of the screen on a phone, left rail on a desktop. */
export default function Toolbar({ tool, setTool, color, setColor, width, setWidth, stamps, stamp, setStamp,
                                  shape = "cloud", setShape, canWrite, compact }) {
  return (
    <div className={`flex ${compact ? "flex-row overflow-x-auto" : "flex-col"} items-center gap-1 bg-neutral-900 text-neutral-100 rounded-xl p-1.5 shadow-lg`}
         data-testid="drawing-toolbar">
      {TOOLS.filter((t) => canWrite || t.id === "pan").map(({ id, label, Icon }) => (
        <button key={id} type="button" title={label} aria-label={label} onClick={() => setTool(id)}
                data-testid={`tool-${id}`}
                className={`w-11 h-11 shrink-0 rounded-lg grid place-items-center transition-colors ${tool === id ? "bg-orange-500 text-white" : "hover:bg-neutral-700"}`}>
          <Icon className="w-5 h-5" />
        </button>
      ))}
      {canWrite && tool === "shape" && (
        <div className={`flex ${compact ? "flex-row" : "flex-col"} items-center gap-1 ${compact ? "pl-2 border-l" : "pt-2 border-t"} border-neutral-700`}
             data-testid="shape-picker">
          {SHAPES.map(({ id, label, Icon }) => (
            <button key={id} type="button" title={label} aria-label={label} onClick={() => setShape?.(id)}
                    data-testid={`shape-${id}`}
                    className={`w-11 h-11 shrink-0 rounded-lg grid place-items-center ${shape === id ? "bg-white text-neutral-900" : "hover:bg-neutral-700"}`}>
              <Icon className="w-5 h-5" />
            </button>
          ))}
        </div>
      )}
      {canWrite && ["pen", "highlighter", "shape", "arrow"].includes(tool) && (
        <div className={`flex ${compact ? "flex-row" : "flex-col"} items-center gap-1 ${compact ? "pl-2 border-l" : "pt-2 border-t"} border-neutral-700`}>
          {COLORS.map((c) => (
            <button key={c} type="button" aria-label={`colour ${c}`} onClick={() => setColor(c)} data-testid={`color-${c.slice(1)}`}
                    className={`w-7 h-7 shrink-0 rounded-full border-2 ${color === c ? "border-white scale-110" : "border-transparent"}`}
                    style={{ background: c }} />
          ))}
          {tool === "pen" && (
            <input type="range" min="1" max="8" value={width} onChange={(e) => setWidth(Number(e.target.value))}
                   className={compact ? "w-16" : "w-9 rotate-90 my-3"} aria-label="Pen width" data-testid="pen-width" />
          )}
        </div>
      )}
      {canWrite && tool === "stamp" && (
        <select value={stamp} onChange={(e) => setStamp(e.target.value)} data-testid="stamp-picker"
                className="text-xs bg-neutral-800 rounded-md px-1 py-1 max-w-[9rem]">
          <optgroup label="Ops Codes">
            {(stamps?.stamps || []).map((s) => <option key={s.name} value={`stamp:${s.name}`}>{s.name}</option>)}
          </optgroup>
          <optgroup label="Product symbols (FRS Codes)">
            {(stamps?.product_symbols || []).map((p) => <option key={p.product_code} value={`symbol:${p.subject}`}>{p.subject}{p.acronym && p.acronym !== p.product_code ? ` · ${p.product_code}` : ""}</option>)}
          </optgroup>
        </select>
      )}
      {compact && <MapPin className="hidden" />}
    </div>
  );
}
